import { createContext, useContext, type ReactNode, type FC, useState } from 'react'
import { emitLocalStorageChange, TypedLocalStorage } from './typed-local-storage.js'

/**
 * Creates a React Context for typed localStorage with test isolation support.
 *
 * This function enables test isolation by allowing different storage instances
 * to be injected via the Provider.
 *
 * <TypedLocalStorageProvider storage={makeTypedInMemoryStorage({ key: 'value' })}> // In tests - each test gets isolated storage
 *   <Component />
 * </TypedLocalStorageProvider>
 */
export function makeTypedLocalStorageContext<Schema extends object>(
  default_: TypedLocalStorage<Schema>,
) {
  const Context = createContext<TypedLocalStorage<Schema> | null>(null)

  type ProviderProps = {
    children: ReactNode
    storage?: TypedLocalStorage<Schema>
  }

  const Provider: FC<ProviderProps> = ({ children, storage }) => {
    const instance = storage ?? new TypedLocalStorage<Schema>()
    return <Context.Provider value={instance}>{children}</Context.Provider>
  }
  function useStorage(): TypedLocalStorage<Schema>
  function useStorage<K extends keyof Schema>(
    key: K,
    initialValue?: Schema[K],
  ): [Schema[K], (value: Schema[K] | ((prev: Schema[K]) => Schema[K])) => void]

  function useStorage<K extends keyof Schema>(key?: K, initialValue?: Schema[K]) {
    const contextStorage = useContext(Context)
    const storage = contextStorage ?? default_

    const [state, setState] = useState<Schema[K] | undefined>(() => {
      if (key === undefined) return initialValue
      const stored = storage.getItem(key)
      if (stored === undefined) {
        if (initialValue === undefined) throw new Error('key not found')
        return initialValue as Schema[K]
      }
      return stored
    })

    emitLocalStorageChange.useListen((detail) => {
      if (key !== undefined && detail.key === String(key)) {
        setState((detail.value as Schema[K]) ?? initialValue)
      }
    })

    if (key === undefined) return storage

    const setValue = (val: Schema[K] | ((prev: Schema[K]) => Schema[K])) => {
      let newValue: Schema[K]
      if (typeof val === 'function') {
        newValue = (val as (prev: Schema[K]) => Schema[K])(storage.getItem(key) as Schema[K])
      } else newValue = val
      setState(newValue)
      storage.setItem(key, newValue)
    }

    return [state, setValue] as const
  }

  return { Provider, useStorage }
}
